/**
 * Map FleetMenu vehicles → Rules Engine vehicle-tags payloads.
 * Uses structural types to avoid circular imports with FleetMenu.
 */

export type FleetVehicleLike = {
  id: string;
  name: string;
  standardSeats?: number;
  wheelchairSeats?: number;
};

export type FleetLike = {
  id: string;
  name: string;
  vehicles: FleetVehicleLike[];
};

export type FleetVehicleTagSeed = {
  vehicle_id: string;
  display_name: string;
  capability_tags: string[];
  fleet_name: string;
};

/**
 * Derive rostering capability tags from fleet vehicle attributes.
 * Wheelchair capacity → accessible; coach-like names → coach; otherwise standard_bus.
 */
export function deriveCapabilityTagsFromFleetVehicle(vehicle: FleetVehicleLike): string[] {
  const tags = new Set<string>();
  const name = (vehicle.name || '').toLowerCase();

  if ((vehicle.wheelchairSeats || 0) > 0) {
    tags.add('accessible');
  }
  if (name.includes('coach') || name.includes('express')) {
    tags.add('coach');
  }
  // Default bus capability so untagged fleet vehicles remain assignable to standard routes.
  if (!tags.has('coach')) {
    tags.add('standard_bus');
  }

  return Array.from(tags);
}

/**
 * Flatten all map fleets into vehicle-tag upsert rows.
 * Prefer stable vehicle.id; fall back to name when id is missing.
 */
export function flattenFleetsToVehicleTagSeeds(fleets: FleetLike[]): FleetVehicleTagSeed[] {
  const out: FleetVehicleTagSeed[] = [];
  const seen = new Set<string>();

  for (const fleet of fleets || []) {
    for (const vehicle of fleet.vehicles || []) {
      const vehicleId = String(vehicle.id || vehicle.name || '').trim();
      if (!vehicleId || seen.has(vehicleId)) continue;
      seen.add(vehicleId);
      out.push({
        vehicle_id: vehicleId,
        display_name: vehicle.name || vehicleId,
        capability_tags: deriveCapabilityTagsFromFleetVehicle(vehicle),
        fleet_name: fleet.name || fleet.id,
      });
    }
  }

  return out;
}
